/**
 * Line commands (tables N and R in the part 10 log): Enter, Shift+Enter, Backspace at a block's start and Delete at
 * its end, as edits for `rewrite` or as a pending block. The canonical form holds no empty paragraph, so Enter at the
 * end of a block (and Shift+Enter at the end of its text) doesn't store anything yet: it leaves a pending block or
 * break, which the next text typed there puts in together with it (N1, N6).
 */
import type { ParsedChapter } from '@cockatrice/core';
import type { SyntaxNode } from '@lezer/common';
import { continuation, emptyParagraphAt, isSpan, openSpans, outsideSpans, spanMarks, splitBlanks } from './rewrite.ts';
import type { Edit, Piece } from './rewrite.ts';

/** A leaf block and where its content lies. */
export interface Leaf {
  readonly node: SyntaxNode;
  readonly contentFrom: number;
  readonly contentTo: number;
}

const ATX = /^ATXHeading[1-6]$/u;
const SETEXT = /^SetextHeading[12]$/u;
/** Containers whose marker a Backspace at their first leaf's start removes (R4b). */
const CONTAINERS: ReadonlySet<string> = new Set(['ListItem', 'Blockquote', 'FootnoteDefinition']);
/** Children that are a container's own markup, not content. */
const MARKS: ReadonlySet<string> = new Set(['ListMark', 'QuoteMark', 'FootnoteMark', 'FootnoteLabel']);

const isSpace = (text: string, at: number): boolean => text.charAt(at) === ' ' || text.charAt(at) === '\t';

/** A node's first and last children (a heading's or centered block's marks): every leaf but a paragraph has both. */
function edges(node: SyntaxNode): { readonly first: { from: number; to: number }; readonly last: { from: number; name: string } } {
  const cursor = node.cursor();
  cursor.firstChild();
  const first = { from: cursor.from, to: cursor.to };
  cursor.parent();
  cursor.lastChild();
  return { first, last: { from: cursor.from, name: cursor.name } };
}

/** `from`–`to` without the blanks at its ends. */
function trimmed(text: string, from: number, to: number): { contentFrom: number; contentTo: number } {
  let contentFrom = from;
  let contentTo = to;
  while (contentFrom < contentTo && isSpace(text, contentFrom)) contentFrom += 1;
  while (contentTo > contentFrom && isSpace(text, contentTo - 1)) contentTo -= 1;
  return { contentFrom, contentTo };
}

/** The content range of a leaf node, or null for a node that isn't one. */
export function leafOf(node: SyntaxNode, text: string): Leaf | null {
  if (node.name === 'Paragraph') return { node, contentFrom: node.from, contentTo: node.to };
  if (ATX.test(node.name)) {
    // Content runs from the `#` run to a closing sequence, if the heading has one.
    const { first, last } = edges(node);
    return { node, ...trimmed(text, first.to, last.name === 'HeaderMark' && last.from > first.from ? last.from : node.to) };
  }
  if (SETEXT.test(node.name)) return { node, contentFrom: node.from, contentTo: text.lastIndexOf('\n', edges(node).last.from - 1) };
  if (node.name === 'CenteredBlock') {
    const { first, last } = edges(node);
    return { node, ...trimmed(text, first.to, last.from) };
  }
  return null;
}

/** The leaf whose content holds `pos`, ends included. */
export function leafAt(parsed: ParsedChapter, pos: number): Leaf | null {
  for (const side of [-1, 1] as const) {
    for (let node: SyntaxNode | null = parsed.tree.resolveInner(pos, side); node !== null; node = node.parent) {
      const leaf = leafOf(node, parsed.text);
      if (leaf !== null) {
        if (pos >= leaf.contentFrom && pos <= leaf.contentTo) return leaf;
        break;
      }
    }
  }
  return null;
}

/**
 * A leaf with marks after its content (a centered block's `<-`) whose line ends at `pos`, where End and a click at the
 * line end put the cursor: for Enter, Shift+Enter and Delete that is the block's content end (*deep-fix r4*).
 */
function leafEndingAt(parsed: ParsedChapter, pos: number): Leaf | null {
  for (let node: SyntaxNode | null = parsed.tree.resolveInner(pos, -1); node !== null; node = node.parent) {
    const leaf = leafOf(node, parsed.text);
    if (leaf !== null) return node.to === pos && leaf.contentTo < pos ? leaf : null;
  }
  return null;
}

/**
 * `pos`, or the content end of a leaf whose closing marks end the line at `pos`; and a point beside the hidden marks of
 * spans that open or close a leaf's content (`*` before `ab`, or after it at the end) is that content's start or end,
 * where the writer sees it (*deep-fix r13*).
 */
function contentPoint(parsed: ParsedChapter, pos: number): number {
  const at = leafEndingAt(parsed, lineContent(parsed, pos))?.contentTo ?? lineContent(parsed, pos);
  const leaf = leafAt(parsed, at);
  if (leaf === null) return at;
  const { opened, closed, ends } = shownContent(parsed, leaf);
  if (at <= opened && opened < closed) return leaf.contentFrom;
  if (ends.has(at) && opened < closed) return leaf.contentTo;
  return at;
}

/**
 * Q1 (*deep-fix r19*): a point among a continuation line's container marks (before a quote's `>`, in its prefix's or an
 * item's indent blanks, where Home puts the cursor while the marks show) is that line's content start; any other point is
 * itself. A first line's marks are the leaf's own (N3).
 */
export function lineContent(parsed: ParsedChapter, pos: number): number {
  const { text, tree } = parsed;
  const mark = (k: number): boolean => text.charAt(k) === ' ' || text.charAt(k) === '\t' || (text.charAt(k) === '>' && tree.resolveInner(k, 1).name === 'QuoteMark');
  const lineStart = text.lastIndexOf('\n', pos - 1) + 1;
  for (let k = lineStart; k < pos; k += 1) if (!mark(k)) return pos;
  let start = pos;
  while (mark(start)) start += 1;
  const leaf = leafAt(parsed, start);
  return leaf !== null && leaf.contentFrom < lineStart ? start : pos;
}

/**
 * A leaf's content as the writer sees it: inside the marks of spans that open or close it; and the points beside those
 * closing marks, which are its end (each point in a run of `*` or `^^`, a link's at its `]` and its end, never inside
 * its destination or title, *deep-fix r14*).
 */
function shownContent(parsed: ParsedChapter, leaf: Leaf): { opened: number; closed: number; ends: ReadonlySet<number> } {
  let opened = leaf.contentFrom;
  for (let span = spanAt(parsed, opened, 1); span !== null; span = spanAt(parsed, opened, 1)) opened = spanMarks(span).open.to;
  let closed = leaf.contentTo;
  const ends = new Set<number>([closed]);
  for (let span = spanAt(parsed, closed, -1); span !== null; span = spanAt(parsed, closed, -1)) {
    const { close } = spanMarks(span);
    if (span.name !== 'Link') for (let point = close.from; point < close.to; point += 1) ends.add(point);
    closed = close.from;
    ends.add(closed);
  }
  return { opened, closed, ends };
}

/**
 * R9 (*deep-fix r13*): a deletion by a key (to a line's start or end, Ctrl+K) within one leaf keeps the leaf's own marks
 * (a heading's `#`, an item's mark, a centered block's `->` and `<-`): they are the block's, not text the key reaches.
 */
export function keyDeletion(parsed: ParsedChapter, from: number, to: number): { from: number; to: number } {
  const leaf = [leafAt(parsed, to), leafAt(parsed, from)].find((l) => l !== null && from >= l.node.from && to <= l.node.to) ?? null;
  if (leaf === null) return { from, to };
  return { from: Math.max(from, Math.min(leaf.contentFrom, to)), to: Math.min(to, Math.max(leaf.contentTo, from)) };
}

/**
 * R4c, R5: `b` joined onto the end of `a`: everything between their contents goes, `b`'s own marks with it, and `a`'s
 * closing marks (a centered block's `<-`) move to the end of the joined text (*deep-fix r4*).
 */
function join(parsed: ParsedChapter, a: Leaf, b: Leaf): Edits {
  const { text } = parsed;
  // Spans of one kind meeting at the seam (`*ab*` and `*cd*`) become one (*deep-fix r13*): their marks there go too.
  let end = a.contentTo;
  let start = b.contentFrom;
  for (;;) {
    const left = spanAt(parsed, end, -1);
    const right = spanAt(parsed, start, 1);
    if (left === null || left.name !== right?.name || left.name === 'Link') break;
    // The canonical form writes each kind of span with one mark (`*`, `**`, `^^`), so the marks at the seam match.
    end = spanMarks(left).close.from;
    start = spanMarks(right).open.to;
  }
  const edits: [Edit, ...Edit[]] = [{ from: end, to: start, insert: [] }];
  const closing = text.slice(a.contentTo, a.node.to);
  if (closing !== '' || b.contentTo < b.node.to) edits.push({ from: b.contentTo, to: b.node.to, insert: closing === '' ? [] : [block(closing)] });
  return edits;
}

/** The outermost span ending at `pos` (`side` -1) or starting at it (`side` 1), or null. */
function spanAt(parsed: ParsedChapter, pos: number, side: -1 | 1): SyntaxNode | null {
  let found: SyntaxNode | null = null;
  for (let node: SyntaxNode | null = parsed.tree.resolveInner(pos, side); node !== null; node = node.parent) {
    if (isSpan(node) && (side === -1 ? node.to : node.from) === pos) found = node;
  }
  return found;
}

/** A container holding nothing but its own marks (an empty item, quote or note) at `pos`, or null. */
function emptyContainerAt(parsed: ParsedChapter, pos: number): SyntaxNode | null {
  for (let node: SyntaxNode | null = parsed.tree.resolveInner(pos, -1); node !== null; node = node.parent) {
    if (!CONTAINERS.has(node.name)) continue;
    let child = node.firstChild;
    while (child !== null && MARKS.has(child.name)) child = child.nextSibling;
    return child === null ? node : null;
  }
  return null;
}

/** A block a Backspace or Delete at a leaf's edge meets: another leaf, or a scene break. */
type Neighbour = { readonly leaf: Leaf } | { readonly scene: SyntaxNode };

/** The leaf or scene break that comes first (`last` false) or last inside `node`, or `node` itself when it is one. */
function edgeBlock(node: SyntaxNode, text: string, last: boolean): Neighbour | null {
  const leaf = leafOf(node, text);
  if (leaf !== null) return { leaf };
  if (node.name === 'SceneBreak') return { scene: node };
  for (let child = last ? node.lastChild : node.firstChild; child !== null; child = last ? child.prevSibling : child.nextSibling) {
    const found = MARKS.has(child.name) ? null : edgeBlock(child, text, last);
    if (found !== null) return found;
  }
  return null;
}

/** The leaf or scene break right before (`after` false) or after `node` in the chapter, wherever it sits. */
function neighbour(node: SyntaxNode, text: string, after: boolean): Neighbour | null {
  for (let at: SyntaxNode | null = node; at !== null; at = at.parent) {
    for (let sibling = after ? at.nextSibling : at.prevSibling; sibling !== null; sibling = after ? sibling.nextSibling : sibling.prevSibling) {
      const found = MARKS.has(sibling.name) ? null : edgeBlock(sibling, text, !after);
      if (found !== null) return found;
    }
  }
  return null;
}

/** The line holding `pos`. */
function lineAround(text: string, pos: number): { from: number; to: number } {
  const from = text.lastIndexOf('\n', pos - 1) + 1;
  const end = text.indexOf('\n', pos);
  return { from, to: end < 0 ? text.length : end };
}

/** Edits, at least one. */
export type Edits = readonly [Edit, ...Edit[]];

/** An empty edit marking where the cursor goes after the edits it is among (K3, K4); else it goes to the first's end. */
export type CursorEdit = Edit & { readonly cursor: true };

/** Removes the line holding `pos` with one of its line ends (R7, N2). */
function removeLine(text: string, pos: number): Edits {
  const line = lineAround(text, pos);
  return line.from > 0 ? [{ from: line.from - 1, to: line.to, insert: [] }] : [{ from: line.from, to: Math.min(text.length, line.to + 1), insert: [] }];
}

const block = (text: string): Piece => ({ kind: 'block', text, prefixed: true, keep: true });

/** A line end, a blank line in the containers (`prefix` without its trailing blanks), and a line end into them. */
function blankLine(prefix: string): string {
  return `\n${prefix.trimEnd()}\n${prefix}`;
}

/** Each line start inside `from`–`to`, after the first. */
function lineStarts(text: string, from: number, to: number): number[] {
  const starts: number[] = [];
  for (let at = text.indexOf('\n', from); at >= 0 && at < to; at = text.indexOf('\n', at + 1)) starts.push(at + 1);
  return starts;
}

/** R4b: a heading's or centered block's own marks removed. */
function ownMarks(leaf: Leaf): Edits {
  const { node } = leaf;
  if (SETEXT.test(node.name)) return [{ from: leaf.contentTo, to: node.to, insert: [] }];
  const edits: [Edit, ...Edit[]] = [{ from: node.from, to: leaf.contentFrom, insert: [] }];
  if (leaf.contentTo < node.to) edits.push({ from: leaf.contentTo, to: node.to, insert: [] });
  return edits;
}

/**
 * R4b: `leaf` out of its innermost own marker: a heading's `#` run, a centered block's marks, or the item, quote or note
 * it opens; blank lines around it so it stands alone (extra blank lines read as one). Null when it has none.
 */
function unwrap(parsed: ParsedChapter, leaf: Leaf): Edits | null {
  const { text } = parsed;
  const { node } = leaf;
  if (node.name !== 'Paragraph') return ownMarks(leaf);
  const container = node.parent;
  if (container === null || !CONTAINERS.has(container.name)) return null;
  let first = container.firstChild;
  while (first !== null && MARKS.has(first.name)) first = first.nextSibling;
  if (first?.from !== node.from) return null;
  // The container's own markup on the leaf's lines: on its first line from the container's start to the content, on
  // later lines its prefix's width.
  const outer = continuation(parsed, container.from);
  const width = continuation(parsed, node.from).length - outer.length;
  const edits: [Edit, ...Edit[]] = [{ from: container.from, to: node.from, insert: [block(blankLine(outer))] }];
  for (const start of lineStarts(text, node.from, node.to)) {
    const from = start + outer.length;
    edits.push({ from, to: Math.min(from + width, node.to), insert: [] });
  }
  edits.push({ from: node.to, to: node.to, insert: [block(blankLine(outer))] });
  return edits;
}

/** The marks an item's or note's first block shares its line with (K5). */
const OPENERS: ReadonlySet<string> = new Set(['ListMark', 'FootnoteMark', 'FootnoteLabel']);

/** The block beside `node` in its container (before it, or `after`), passing the container's marks; null at its edge. */
function siblingBlock(node: SyntaxNode, after: boolean): SyntaxNode | null {
  let sibling = after ? node.nextSibling : node.prevSibling;
  while (sibling !== null && MARKS.has(sibling.name)) sibling = after ? sibling.nextSibling : sibling.prevSibling;
  return sibling;
}

/**
 * Part 10b, K1–K5: the edits taking empty paragraph `node` away with one key: its line with the blank line before it
 * (`back`) or after it, the other side when it has no block on that one in its container; only its no-break spaces
 * when it has a block on neither side, or when its line opens the item or note it is first in (K5).
 */
function removeEmpty(node: SyntaxNode, back: boolean, keep?: number): Edits {
  const edits = removal(node, back);
  // K3, K4 (*deep-fix r1*): a cursor outside what goes (in a block that may be in another container) stays where it was:
  // an empty edit at it marks where the cursor goes after them. One at an edge of what goes is that edit's end.
  if (keep === undefined || !edits.every((edit) => keep < edit.from || keep > edit.to)) return edits;
  const at: CursorEdit = { from: keep, to: keep, insert: [], cursor: true };
  const [first, ...rest] = edits;
  // In order: the rewrite takes its edits sorted.
  return keep < first.from ? [at, ...edits] : [first, ...rest, at];
}

/** K1–K5: what removing empty paragraph `node` deletes. */
function removal(node: SyntaxNode, back: boolean): Edits {
  const before = siblingBlock(node, false);
  const after = siblingBlock(node, true);
  // K5: first in an item or note, it shares its line with the container's mark.
  if (before === null && OPENERS.has(node.prevSibling?.name ?? '')) return [{ from: node.from, to: node.to, insert: [] }];
  // To the next block's start: the blank line and the next line's prefix go, its own prefix takes their place.
  if (after !== null && (before === null || !back)) return [{ from: node.from, to: after.from, insert: [] }];
  if (before !== null) return [{ from: before.to, to: node.to, insert: [] }];
  // K5: alone in its container or chapter.
  return [{ from: node.from, to: node.to, insert: [] }];
}

/** R4: Backspace with the cursor at `pos`; null when CodeMirror's own deletion applies (R6). */
export function backspace(parsed: ParsedChapter, pos: number): Edits | readonly [] | null {
  const { text } = parsed;
  if (emptyContainerAt(parsed, pos) !== null && lineAround(text, pos).to === pos) return removeLine(text, pos);
  const point = contentPoint(parsed, pos);
  // K1: on an empty paragraph, it goes.
  const empty = emptyParagraphAt(parsed, point);
  if (empty !== null) return removeEmpty(empty, true);
  const leaf = leafAt(parsed, point);
  if (leaf === null) return null;
  if (point !== leaf.contentFrom) return breakJoin(parsed, pos);
  const unwrapped = unwrap(parsed, leaf);
  if (unwrapped !== null) return unwrapped;
  const before = neighbour(leaf.node, text, false);
  if (before === null) return [];
  if ('scene' in before) return [{ from: before.scene.from, to: leaf.node.from, insert: [] }];
  // K3: an empty paragraph before goes, not joined (its no-break spaces would stay as text).
  const emptyBefore = emptyParagraphAt(parsed, before.leaf.contentFrom);
  if (emptyBefore !== null) return removeEmpty(emptyBefore, false, pos);
  return join(parsed, before.leaf, leaf);
}

/**
 * The start of the backslash hard break `pos` is right after, at its next line's content start (past the line's
 * container marks); else null.
 */
export function breakBefore(parsed: ParsedChapter, pos: number): number | null {
  const { text } = parsed;
  const lineFrom = text.lastIndexOf('\n', pos - 1) + 1;
  if (!/^[ \t>]*$/u.test(text.slice(lineFrom, pos))) return null;
  // The innermost node ending at the line's start: a break, if one ends the line before.
  const node = parsed.tree.resolveInner(lineFrom, -1);
  return node.name === 'HardBreak' && text.charAt(node.from) === '\\' ? node.from : null;
}

/**
 * R8 (*deep-fix r6*): Backspace at a line's content start right after a backslash hard break, whose `\` is hidden
 * there: the break goes whole (its `\`, its line end and the line's container marks), so no `\` is left as text.
 */
function breakJoin(parsed: ParsedChapter, pos: number): Edits | null {
  const from = breakBefore(parsed, pos);
  return from === null ? null : [{ from, to: pos, insert: [] }];
}

/** R5: Delete with the cursor at `pos`; null when CodeMirror's own deletion applies (R6). */
export function deleteForward(parsed: ParsedChapter, cursor: number): Edits | readonly [] | null {
  const { text } = parsed;
  if (emptyContainerAt(parsed, cursor) !== null && lineAround(text, cursor).to === cursor) return removeLine(text, cursor);
  const pos = contentPoint(parsed, cursor);
  // K2: on an empty paragraph, it goes.
  const empty = emptyParagraphAt(parsed, pos);
  if (empty !== null) return removeEmpty(empty, false);
  const leaf = leafAt(parsed, pos);
  if (leaf === null) return null;
  if (pos !== leaf.contentTo) return null;
  const after = neighbour(leaf.node, text, true);
  if (after === null) return [];
  if ('scene' in after) return [{ from: leaf.node.to, to: after.scene.to, insert: [] }];
  // K4: an empty paragraph after goes, not joined.
  const emptyAfter = emptyParagraphAt(parsed, after.leaf.contentFrom);
  if (emptyAfter !== null) return removeEmpty(emptyAfter, true, cursor);
  return join(parsed, leaf, after.leaf);
}

/** A block or break Enter or Shift+Enter left at the end of a leaf, put in with the next text typed there (N1, N6). */
export interface Pending {
  /** Where the cursor stays: the leaf's content end, or its start for a block above. */
  readonly at: number;
  /** Where the text goes in: `at`, or the leaf's start for a block above (before its heading or centered mark). */
  readonly from: number;
  /** The markup put in with the text: before it, or after it for a block above. */
  readonly open: readonly Piece[];
  /** A new block after the leaf, a hard break, or a new block above it (*deep-fix r3*, the user's choice). */
  readonly kind: 'block' | 'break' | 'above';
  /** How many containers a further Enter has left (N1: Enter on a pending block steps out of one). */
  readonly out: number;
  /**
   * Fix-next 6, table S (S2, S4): markup put in after the text, a blank line in the containers outside those left, when
   * what they held after the leaf goes on there: a list or quote of its own after the text.
   */
  readonly after?: readonly Piece[];
}

/** N1, N6: the edit putting `insert` in with what is pending: after its markup, or before it for a block above. */
export function pendingEdit(pending: Pending, insert: readonly Piece[]): Edit {
  const open = pending.open.map((p) => ({ ...p, prefixed: true }));
  if (pending.kind === 'above') return { from: pending.from, to: pending.from, insert, after: open };
  const edit = { from: pending.from, to: pending.from, insert: [...open, ...insert] };
  return pending.after === undefined ? edit : { ...edit, after: pending.after.map((p) => ({ ...p, prefixed: true })) };
}

/** The containers holding `node`, innermost first. */
function containers(node: SyntaxNode): SyntaxNode[] {
  const out: SyntaxNode[] = [];
  for (let at = node.parent; at !== null; at = at.parent) if (CONTAINERS.has(at.name)) out.push(at);
  return out;
}

/**
 * The pending block after `leaf`, outside the container `left` (none: in the leaf's own), inside `inner`: a new item when
 * `inner` is an item the leaf ends (or one the writer stepped out to), else a paragraph.
 */
function pendingIn(parsed: ParsedChapter, leaf: Leaf, left: SyntaxNode | undefined, inner: SyntaxNode | undefined, out: number, split = false): Pending {
  if (inner?.name === 'ListItem' && (out > 0 || inner.to === leaf.node.to)) {
    // An item's first child is its mark.
    const mark = inner.cursor();
    mark.firstChild();
    const marker = parsed.text.slice(mark.from, mark.to);
    const number = /^\d+/u.exec(marker);
    const next = number === null ? marker : `${Number.parseInt(number[0], 10) + 1}${marker.slice(-1)}`;
    return { at: leaf.contentTo, from: leaf.node.to, open: [block(`\n${continuation(parsed, inner.from)}${next} `)], kind: 'block', out };
  }
  const prefix = continuation(parsed, left === undefined ? leaf.contentTo : left.from);
  // The new block goes after the leaf's closing marks, if it has any (a centered block's `<-`, *deep-fix r4*).
  const pending: Pending = { at: leaf.contentTo, from: leaf.node.to, open: [block(blankLine(prefix))], kind: 'block', out };
  // Table S (S2, S4): a blank line after the text, so what the containers left held goes on in its own.
  return split ? { ...pending, after: [block(`\n${prefix.trimEnd()}`)] } : pending;
}

/** The pending block after `leaf` in its own containers (N1). */
function pendingHere(parsed: ParsedChapter, leaf: Leaf): Pending {
  return pendingIn(parsed, leaf, undefined, containers(leaf.node)[0], 0);
}

/**
 * R11 (*deep-fix r21*): a deletion of all the text of a paragraph that follows another in the same container leaves no
 * paragraph the canonical form can store; the block the writer still sees is pending after the one before, as Enter at
 * its end would leave it, so the next text typed is a paragraph of its own. Null for any other deletion.
 */
export function emptiedPending(parsed: ParsedChapter, from: number, to: number): Pending | null {
  const leaf = leafAt(parsed, to);
  if (leaf?.node.name !== 'Paragraph' || from < leaf.node.from) return null;
  const { opened, closed } = shownContent(parsed, leaf);
  if (from > opened || to < closed) return null;
  const before = neighbour(leaf.node, parsed.text, false);
  if (before === null || 'scene' in before || before.leaf.node.name !== 'Paragraph' || before.leaf.node.parent?.from !== leaf.node.parent?.from) return null;
  return pendingHere(parsed, before.leaf);
}

/**
 * Fix-next 6, table S (the user's choice 2026-10-15: the text goes where the line shows): what stepping `out` of `leaf`'s
 * containers (`holders`, innermost first) leaves after it. `clear` when nothing of theirs follows the leaf (S1); `split`
 * when later items of a left item's list (S2) or later blocks of a left quote (S4) follow, and go on as a list or quote of
 * their own after the text; null when the text can't be written there (S3: later blocks in a left item; S5: in a left
 * note, which can't resume; S6, below: a new item of an outer list, which would take the rest in).
 */
function stepOut(parsed: ParsedChapter, leaf: Leaf, holders: readonly SyntaxNode[], out: number): 'clear' | 'split' | null {
  const rest = (from: number, to: number): boolean => !/^[\s>]*$/u.test(parsed.text.slice(from, to));
  let split = false;
  for (const holder of holders.slice(0, out)) {
    const own = rest(leaf.node.to, holder.to);
    if (own && holder.name !== 'Blockquote') return null;
    // An item's list is its parent; later items there follow the leaf too.
    const list = holder.name === 'ListItem' ? holder.parent : null;
    split ||= own || (list !== null && rest(holder.to, list.to));
  }
  return split ? 'split' : 'clear';
}

/**
 * The pending block after `leaf`, `out` containers out (N1). Null when there are not that many containers, or when the
 * text can't be written there (fix-next 6, table S): a further Enter there makes the empty paragraph (part 10b, E1).
 */
export function pendingBlock(parsed: ParsedChapter, leaf: Leaf, out: number): Pending | null {
  const holders = containers(leaf.node);
  if (out > holders.length) return null;
  const step = stepOut(parsed, leaf, holders, out);
  // S6: a new item of the outer list would hold what follows in the list left.
  if (step === null || (step === 'split' && holders[out]?.name === 'ListItem')) return null;
  return pendingIn(parsed, leaf, holders[out - 1], holders[out], out, step === 'split');
}

/**
 * The result of Enter or Shift+Enter: edits to make, a pending block or break, or nothing. `then` (fix-next 4): a block
 * pending in the text the edits leave, set once they are made.
 */
export type LineResult = { readonly edits: Edits; readonly then?: Pending } | { readonly pending: Pending } | null;

/**
 * N3: whether `pos` is in a link's or image's destination or title, or a note reference (its label), ends included:
 * a line end there would break the construct the writer is editing (*deep-fix r1*).
 */
function inValue(parsed: ParsedChapter, pos: number): boolean {
  for (const side of [-1, 1] as const) {
    for (let node: SyntaxNode | null = parsed.tree.resolveInner(pos, side); node !== null; node = node.parent) {
      if (node.name === 'FootnoteReference' && pos > node.from && pos < node.to) return true;
      if (node.name === 'Link' || node.name === 'Image') {
        // The text ends at the second mark, `]`; after it come `(`, the destination, the title and `)`.
        for (const [i, mark] of node.getChildren('LinkMark').entries()) if (i === 1 && pos > mark.from && pos < node.to) return true;
      }
    }
  }
  return false;
}

/** N1–N3, N7: Enter with the cursor at `pos`, given what is pending there. */
export function enter(parsed: ParsedChapter, cursor: number, pending: Pending | null): LineResult {
  if (inValue(parsed, cursor)) return null;
  // The line end after a centered block's `<-` is its content end; what is pending stays where the cursor is.
  const pos = contentPoint(parsed, cursor);
  const result = enterAt(parsed, pos, pending?.at === cursor ? { ...pending, at: pos } : pending);
  return result !== null && 'pending' in result ? { pending: { ...result.pending, at: cursor } } : result;
}

/** Enter at a content point. */
function enterAt(parsed: ParsedChapter, pos: number, pending: Pending | null): LineResult {
  const { text } = parsed;
  if (emptyContainerAt(parsed, pos) !== null) {
    // Fix-next 4: on the chapter's first line nothing is left before the next block, so the cursor would stand at its
    // start and the next text join it. The line the writer ended is a block pending above that one, as N1 leaves at a
    // paragraph's start; a later line leaves the blank line before (table C).
    const line = lineAround(text, pos);
    const after = line.from === 0 && text.slice(line.to).trim() !== '';
    return after ? { edits: removeLine(text, pos), then: { at: 0, from: 0, open: [block(blankLine(''))], kind: 'above', out: 0 } } : { edits: removeLine(text, pos) };
  }
  const leaf = leafAt(parsed, pos);
  if (leaf === null) return null;
  // Enter again on a block pending above changes nothing.
  if (pending?.at === pos && pending.kind === 'above') return null;
  if (pending?.at === pos && pending.kind === 'block') {
    const next = pendingBlock(parsed, leaf, pending.out + 1);
    return next === null ? null : { pending: next };
  }
  if (pos === leaf.contentTo) {
    if (ATX.test(leaf.node.name) && leaf.contentFrom === leaf.contentTo) return { edits: ownMarks(leaf) };
    // A pending break becomes a pending block.
    return { pending: pendingHere(parsed, leaf) };
  }
  // N1 (*deep-fix r3*, the user's choice): at a paragraph's start, a block pending above it; in an item, an item before.
  if (pos === leaf.contentFrom && containers(leaf.node)[0]?.name !== 'ListItem') {
    const start = leaf.node.from;
    return { pending: { at: pos, from: start, open: [block(blankLine(continuation(parsed, start)))], kind: 'above', out: 0 } };
  }
  // N1 (*deep-fix r13, r14*): a span the split is inside goes on in the next block, closed before the break and the
  // blanks beside it (which stay text, outside the marks), opened after them; a soft line end there is the break's.
  // At a span's edge the split goes outside its marks (*deep-fix r14*).
  const edge = outsideSpans(parsed, pos);
  if (edge !== pos) return { edits: [{ from: edge, to: edge, insert: splitPieces(parsed, leaf, edge) }] };
  const spans = openSpans(parsed, pos);
  if (spans.close.length === 0) return { edits: [{ from: pos, to: pos, insert: splitPieces(parsed, leaf, pos) }] };
  const near = splitBlanks(parsed, pos, pos, true);
  const blanks = (blank: string): Piece[] => (blank === '' ? [] : [{ kind: 'text', text: blank }]);
  const insert = [...spans.close, ...blanks(near.before), ...splitPieces(parsed, leaf, pos), ...blanks(near.after), ...spans.open];
  return { edits: [{ from: near.from, to: near.to, insert }] };
}

/** N1: the markup that splits `leaf` at `pos` into two blocks of its kind. */
function splitPieces(parsed: ParsedChapter, leaf: Leaf, pos: number): Piece[] {
  const { node } = leaf;
  const prefix = continuation(parsed, pos);
  if (ATX.test(node.name) || SETEXT.test(node.name)) {
    const level = Number(node.name.slice(-1));
    return [block(`${blankLine(prefix)}${'#'.repeat(level)} `)];
  }
  if (node.name === 'CenteredBlock') return [block(`<-${blankLine(prefix)}->`)];
  // A paragraph: the block Enter at its end would leave (a new item at the end of one, a paragraph elsewhere).
  return [...pendingHere(parsed, leaf).open];
}

/** Whether a backslash hard break starts at `pos`. */
function breakAt(parsed: ParsedChapter, pos: number): boolean {
  const node = parsed.tree.resolveInner(pos, 1);
  return node.name === 'HardBreak' && node.from === pos && parsed.text.charAt(pos) === '\\';
}

/** A backslash hard break at `pos`, and the container marks its next line starts with. */
export function breakPieces(parsed: ParsedChapter, pos: number): Piece[] {
  return [
    { kind: 'inline', text: '\\\n', keep: true },
    { kind: 'block', text: continuation(parsed, pos), keep: true },
  ];
}

/** N5, N6: Shift+Enter with the cursor at `pos`. */
export function shiftEnter(parsed: ParsedChapter, cursor: number): LineResult {
  if (inValue(parsed, cursor)) return null;
  // At a span's content edge the break goes outside its marks, as Enter's split does (*deep-fix r18*).
  const pos = outsideSpans(parsed, contentPoint(parsed, cursor));
  const leaf = leafAt(parsed, pos);
  // An ATX heading holds no line break (*deep-fix r4*): the key changes nothing there.
  if (leaf === null || ATX.test(leaf.node.name)) return null;
  const open = breakPieces(parsed, pos);
  // At a line's end, the content's or one a backslash break ends (*deep-fix r9*): a new line pending there.
  // Q3 (*deep-fix r19*): a soft line's end is a line's end too.
  const lineEnd = pos < leaf.contentTo && parsed.text.charAt(pos) === '\n';
  if (pos === leaf.contentTo || breakAt(parsed, pos) || lineEnd) return { pending: { at: cursor, from: pos, open, kind: 'break', out: 0 } };
  return { edits: [{ from: pos, to: pos, insert: open }] };
}
